import { BULK_TENTATIVE_DATE_ASSIGNMENT_TABLE_QUERY } from '@/components/bulkQueue/tables/tentativeDate/BulkTentativeDateAssignmentTable';
import {
    BulkUpdateVisitRequestTentativeDateMutation,
    BulkUpdateVisitRequestTentativeDateMutationVariables,
    BulkUpdateVisitTentativeDateMutation,
    BulkUpdateVisitTentativeDateMutationVariables,
} from '@/components/visitRequests/mutations/updateTentativeDate.gql.types';
import { GraphInputHelpers } from '@/fetching/graphql/GraphInputHelpers';
import { ALL_VISIT_COUNTS } from '@/pages/AllVisitsPage/AllVisits.gql';
import { errorToast } from '@/toast';
import { gql, useMutation } from '@apollo/client';
import type { MutationHookOptions } from '@apollo/client/react/types/types';
import { UpdateVisitRequestTentativeDateInput, UpdateVisitTentativeDateInput } from 'gql-types';
import { Dictionary } from 'lodash';
import moment from 'moment';

// Individual Visit Request Tentative Date Update Mutation
export const useUpdateTentativeDate = ({ visitRequestId }: { visitRequestId: string }) => {
    // Assign clinician mutation.
    const [mutate, result] = useMutation(UPDATE_TENTATIVE_DATE_MUTATION, {
        variables: {
            visitRequestId,
        },
        refetchQueries: [BULK_TENTATIVE_DATE_ASSIGNMENT_TABLE_QUERY, ALL_VISIT_COUNTS],
        onError: (error) => {
            errorToast(error.message);
        },
    });

    function updateTentativeDate({ tentativeDate }: { tentativeDate: Date | string | null }) {
        return mutate({
            variables: {
                tentativeDate: GraphInputHelpers.date(tentativeDate),
            },
            refetchQueries: [ALL_VISIT_COUNTS, BULK_TENTATIVE_DATE_ASSIGNMENT_TABLE_QUERY],
        });
    }

    return { updateTentativeDate, ...result };
};

export interface IAssignTentativeDateInput {
    visitRequestId: string;
    tentativeDate: Date | string | null;
}

// Bulk Visit Request Tentative Date Update Mutation
export const useBulkUpdateTentativeDate = () => {
    const [mutate, results] = useMutation<
        BulkUpdateVisitRequestTentativeDateMutation,
        BulkUpdateVisitRequestTentativeDateMutationVariables
    >(BULK_UPDATE_TENTATIVE_DATE_MUTATION, {
        onError: (error) => {
            errorToast(error.message);
        },
        refetchQueries: [ALL_VISIT_COUNTS, BULK_TENTATIVE_DATE_ASSIGNMENT_TABLE_QUERY],
    });

    const updateTentativeDates = ({
        inputs,
        options,
    }: {
        inputs: UpdateVisitRequestTentativeDateInput[];
        options?: Pick<MutationHookOptions, 'onError' | 'onCompleted'>;
    }) => {
        return mutate({
            variables: {
                input: {
                    visitRequests: inputs.map((input) => ({
                        visitRequestId: input.visitRequestId,
                        tentativeDate: GraphInputHelpers.date(input.tentativeDate ?? null),
                    })),
                },
            },
            ...options,
        });
    };

    return { updateTentativeDates, ...results };
};

const VisitRequestTentativeDateUpdateFragment = gql`
    fragment VisitRequestTentativeDateUpdateFragment on VisitRequest {
        id
        state
        workflowStep
        tentativeDate
        effectiveDate
    }
`;
const UPDATE_TENTATIVE_DATE_MUTATION = gql`
    mutation UpdateVisitRequestTentativeDate($visitRequestId: UUID!, $tentativeDate: Date) {
        updateVisitRequestTentativeDate(
            input: { visitRequestId: $visitRequestId, tentativeDate: $tentativeDate }
        ) {
            ...VisitRequestTentativeDateUpdateFragment
        }
    }
    ${VisitRequestTentativeDateUpdateFragment}
`;

export const BULK_UPDATE_TENTATIVE_DATE_MUTATION = gql`
    mutation BulkUpdateVisitRequestTentativeDate(
        $input: BulkUpdateVisitRequestTentativeDateInput!
    ) {
        bulkUpdateVisitRequestTentativeDate(input: $input) {
            results {
                resource {
                    ...VisitRequestTentativeDateUpdateFragment
                }
                errors {
                    message
                }
                success
            }
        }
    }
    ${VisitRequestTentativeDateUpdateFragment}
`;

const VisitTentativeDateUpdateFragment = gql`
    fragment VisitTentativeDateUpdateFragment on Visit {
        id
        state
        outcome
        workflowStep
        tentativeDate
        effectiveDate
    }
`;

export const BULK_UPDATE_VISIT_TENTATIVE_DATE_MUTATION = gql`
    mutation BulkUpdateVisitTentativeDate($input: BulkUpdateVisitTentativeDateInput!) {
        bulkUpdateVisitTentativeDate(input: $input) {
            results {
                resource {
                    ...VisitTentativeDateUpdateFragment
                }
                errors {
                    message
                }
                success
            }
        }
    }
    ${VisitTentativeDateUpdateFragment}
`;

export const useBulkUpdateVisitTentativeDate = () => {
    const [mutate, results] = useMutation<
        BulkUpdateVisitTentativeDateMutation,
        BulkUpdateVisitTentativeDateMutationVariables
    >(BULK_UPDATE_VISIT_TENTATIVE_DATE_MUTATION, {
        onError: (error) => {
            errorToast(error.message);
        },
        refetchQueries: [ALL_VISIT_COUNTS, BULK_TENTATIVE_DATE_ASSIGNMENT_TABLE_QUERY],
    });

    const updateVisitTentativeDates = ({
        inputs,
        options,
    }: {
        inputs: UpdateVisitTentativeDateInput[];
        options?: Pick<MutationHookOptions, 'onError' | 'onCompleted'>;
    }) => {
        return mutate({
            variables: {
                input: {
                    visits: inputs.map((input) => ({
                        visitId: input.visitId,
                        tentativeDate: GraphInputHelpers.date(input.tentativeDate ?? null),
                    })),
                },
            },
            ...options,
        });
    };

    return { updateVisitTentativeDates, ...results };
};

/* Helpers */
export const renderBulkDateAssignmentSummaryString = (
    selectedAssignments: Dictionary<{ date: Date }[]>,
) => {
    const dates = new Set<Date>();
    Object.values(selectedAssignments).forEach((visitRequestRecommendations) => {
        dates.add(visitRequestRecommendations[0].date);
    });

    const visitString = Array.from(dates)
        .map((d) => moment(d).format('MM/DD/YYYY'))
        .join(', ');
    return `Assign date${dates.size > 1 ? 's' : ''} to ${
        Object.keys(selectedAssignments).length
    } visits: ${visitString}`;
};
